'use client';

import { Switch } from '../../components/ui/switch';
import SubjectTabs from '../generic/SubjectTabs';
import { useState, useEffect } from 'react';
import StatCell from '../generic/StatCell';
import { secondsToMinutes } from '../../../../../apps/frontend/src/utils/utilFunctions';
import moment from 'moment';
import TableHeader from '../../components/generic/TableHeader';
import { QSTATUS } from '../../../../../apps/frontend/src/utils/strings';
import { PercentIcon } from '@phosphor-icons/react';
import TimeAnalysisSkeleton from './TimeAnalysisHourSkeleton';
import NoData from '../generic/NoData';
import { useNavigate } from 'react-router-dom';

const TimeAnalysisTestBreakdown = ({ data, loading, headers, isDark, onResetFilters }) => {
  const [activeSubject, setActiveSubject] = useState('overall');
  const [showPercentage, setShowPercentage] = useState(true);
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Extract unique subjects from the data
    const subjectsFromData = [];
    const seenSubjects = new Set();

    data.forEach((test) => {
      test.qualityOfTimeSpent?.forEach((subject) => {
        if (subject.subjectMeta && !seenSubjects.has(subject.subjectMeta._id)) {
          seenSubjects.add(subject.subjectMeta._id);
          subjectsFromData.push({
            _id: subject.subjectMeta._id,
            title: subject.subjectMeta.title,
            icon: subject.subjectMeta.icon,
            color: subject.subjectMeta.color,
            gradient: subject.subjectMeta.gradient, // ✅ add this
            gradientDark: subject.subjectMeta.gradientDark, // ✅ add this
          });
        }
      });
    });

    // Use the passed headers if available, otherwise use subjects from data
    const finalSubjects = headers && headers.length > 0 ? headers : subjectsFromData;
    setAvailableSubjects(finalSubjects);
  }, [data, headers]);

  if (loading) return <TimeAnalysisSkeleton />;
  if (!data || data.length === 0) return <NoData onResetAll={onResetFilters} />;

  return (
    <div>
      <TableHeader
        headerTitle="Test-wise Breakdown"
        headerSwitch={
          <span className="flex items-center gap-1">
            <PercentIcon size={16} weight="bold" />
            Show Percentage
          </span>
        }
        headerSwitchCase={showPercentage}
        headerSwitchHandler={setShowPercentage}
        showBottomBorder={false}
      />

      {/* SubjectTabs Scroll Container */}
      <div className="overflow-x-auto border-r border-l border-[var(--q3-stroke-light)] bg-white mb-[-1px]">
        <div className="min-w-0 md:min-w-0">
          <SubjectTabs activeSubject={activeSubject} onSubjectChange={setActiveSubject} subjects={availableSubjects} />
        </div>
      </div>

      {/* Table Content Scroll Container */}
      <div className="overflow-x-auto">
        <div className="min-w-[650px] border-r border-l border-b border-[var(--q3-stroke-light)] rounded-b-lg overflow-hidden">
          {/* Table Head */}
          <div className="bg-[var(--q3-surface-dim)] border-b border-[var(--q3-stroke-light)]">
            <div className="grid grid-cols-[220px_1fr_1fr_1fr] divide-x divide-[var(--q3-stroke-light)] text-sm font-medium text-[var(--q3-neutral-default)]">
              <div className="p-3 flex items-center justify-start">Test Title</div>

              <div className="p-3 text-center text-[var(--q3-green-normal)]">
                <div>Time Spent on</div>
                <div>Correct Attempts</div>
              </div>

              <div className="p-3 text-center text-[var(--q3-danger-normal)]">
                <div>Time Spent on</div>
                <div>Incorrect Attempts</div>
              </div>

              <div className="p-3 text-center text-[var(--q3-neutral-light)]">
                <div>Time Spent on</div>
                <div>Unattempted Qs</div>
              </div>
            </div>
          </div>

          {/* Table Body */}
          {data.map((test, index) => {
            const subjectData = test.qualityOfTimeSpent?.find(
              (sub) =>
                sub.subjectMeta?._id === activeSubject ||
                (activeSubject === 'overall' && sub.subjectMeta?._id === 'overall'),
            );

            if (!subjectData) return null;

            return (
              <div key={index} className="border-b border-[var(--q3-stroke-light)] last:border-b-0">
                <div className="grid grid-cols-[220px_1fr_1fr_1fr] divide-x divide-[var(--q3-stroke-light)] items-center text-sm text-[var(--q3-neutral-default)]">
                  {/* Test Title (Clickable) */}
                  <div
                    className="p-3 cursor-pointer hover:bg-[var(--q3-surface-hover)]"
                    onClick={() => navigate(`/${test.packId}/${test.testId}/analysis`)}
                  >
                    <div className="font-medium truncate">{test.testTitle}</div>
                    <div className="text-xs text-[var(--q3-neutral-light)]">
                      {moment(test.submittedAt).format('D MMM')}
                    </div>
                  </div>

                  {/* Correct Attempts */}
                  <div className="text-center text-green-600">
                    <StatCell
                      color="green"
                      top={secondsToMinutes(subjectData.correct)}
                      bottom={secondsToMinutes(subjectData.totalTimeSpent)}
                      showPercentage={showPercentage}
                      textAppend="min"
                      compact={true}
                      {...QSTATUS.CORRECT}
                    />
                  </div>

                  {/* Incorrect Attempts */}
                  <div className="text-center text-red-600">
                    <StatCell
                      color="red"
                      top={secondsToMinutes(subjectData.inCorrect)}
                      bottom={secondsToMinutes(subjectData.totalTimeSpent)}
                      showPercentage={showPercentage}
                      textAppend="min"
                      compact={true}
                      {...QSTATUS.INCORRECT}
                    />
                  </div>

                  {/* Unattempted Qs */}
                  <div className="text-center text-blue-600">
                    <StatCell
                      color="blue"
                      top={secondsToMinutes(subjectData.notAnswered)}
                      bottom={secondsToMinutes(subjectData.totalTimeSpent)}
                      showPercentage={showPercentage}
                      textAppend="min"
                      themedText={true}
                      compact={true}
                      {...QSTATUS.NOT_ATTEMPTED}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimeAnalysisTestBreakdown;
